import React, { useState, useEffect } from 'react';
import { MapPin, Calendar, Clock, Phone, Heart } from 'lucide-react';

const WeddingInvitation = () => {
  const [guestData, setGuestData] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    attendingDay1: false,
    attendingDay2: false,
    notAttending: false,
    needsTransfer: false,
    stayingOvernight: false,
    allergies: '',
    alcoholPreference: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [hasValidToken, setHasValidToken] = useState(false);
  const [isCheckingToken, setIsCheckingToken] = useState(true);
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);

  useEffect(() => {
    // Check if running locally (localhost or 127.0.0.1)
    const isLocalhost = window.location.hostname === 'localhost' ||
                        window.location.hostname === '127.0.0.1' ||
                        window.location.hostname === '';

    // Get token from URL
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');

    if (isLocalhost && !token) {
      // Skip token validation for local development
      setIsCheckingToken(false);
      setHasValidToken(true);
      setGuestData({
        name: 'Тестовый Гость',
        ending: 'ой'
      });
    } else if (token) {
      fetchGuestData(token);
      fetchRsvpData(token);
    } else {
      setIsCheckingToken(false);
      setHasValidToken(false);
    }
  }, []);

  const fetchGuestData = async (token) => {
    try {
      const response = await fetch(`/.netlify/functions/get-guest?token=${token}`);
      if (response.ok) {
        const data = await response.json();
        setGuestData(data);
        setHasValidToken(true);
      } else {
        setHasValidToken(false);
      }
    } catch (error) {
      console.error('Error fetching guest data:', error);
      setHasValidToken(false);
    } finally {
      setIsCheckingToken(false);
    }
  };

  const fetchRsvpData = async (token) => {
    try {
      const response = await fetch(`/.netlify/functions/get-rsvp?token=${token}`);
      if (response.ok) {
        const data = await response.json();
        // Update form with saved data
        setFormData({
          name: data.submittedName || '',
          attendingDay1: data.attendingDay1 || false,
          attendingDay2: data.attendingDay2 || false,
          notAttending: data.notAttending || false,
          needsTransfer: data.needsTransfer || false,
          stayingOvernight: data.stayingOvernight || false,
          allergies: data.allergies || '',
          alcoholPreference: data.alcoholPreference || ''
        });
        setIsFormSubmitted(true);
      }
      // If 404, form hasn't been submitted yet - that's okay
    } catch (error) {
      console.error('Error fetching RSVP data:', error);
      // Not critical - form can still be filled
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');

    try {
      const response = await fetch('/.netlify/functions/submit-rsvp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          ...formData
        })
      });

      if (response.ok) {
        setSubmitStatus('success');
        setIsFormSubmitted(true);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openMap = () => {
    window.open('https://yandex.ru/maps/?text=д.Забелинская, Архангельская область, База отдыха Paradise', '_blank');
  };

  // Show loading state while checking token
  if (isCheckingToken) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl" style={{fontFamily: 'Raleway, sans-serif'}}>Загрузка...</p>
        </div>
      </div>
    );
  }

  // Show placeholder page if no valid token
  if (!hasValidToken) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-6">
        <div className="max-w-2xl text-center">
          <p className="text-xl leading-relaxed" style={{fontFamily: 'Raleway, sans-serif'}}>
            Дорогой гость! Похоже, что ссылка, по которой вы перешли, некорректна. Пожалуйста, используйте индивидуальную ссылку из вашего приглашения или свяжитесь с нами для получения помощи.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white font-serif">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-40"
          style={{
            backgroundImage: 'url("https://github.com/sergey-savelyev/weddingzal-cdn/blob/main/IMG_3669.jpg?raw=true")',
            filter: 'grayscale(100%)'
          }}
        />
        <div className="relative z-10 text-center animate-fade-in flex flex-col items-center justify-center">
          <h1 className="text-8xl font-normal italic tracking-wider" style={{fontFamily: 'Cormorant Garamond, serif'}}>
            Тимофей
          </h1>
          <br />
          <div className="text-4xl font-normal my-6">&</div>
          <h1 className="text-8xl font-normal italic tracking-wider" style={{fontFamily: 'Cormorant Garamond, serif'}}>
            Дарья
          </h1>
        </div>
      </section>

      {/* Invitation Section */}
      <section className="py-24 px-6 bg-white text-black">
        <div className="max-w-4xl mx-auto text-center space-y-8 animate-slide-up">
          <h2 className="text-6xl font-normal italic tracking-wide mb-12" style={{fontFamily: 'Cormorant Garamond, serif'}}>
            Приглашение
          </h2>
          <div className="h-px w-32 bg-black mx-auto mb-8"></div>

          <p className="text-xl font-bold leading-relaxed pt-6" style={{fontFamily: 'Raleway, sans-serif'}}>
            Дорог{guestData?.ending || 'ие'} {guestData?.name?.split(' ')[0] || 'гость/гости'}
          </p>

          <p className="text-xl font-normal leading-relaxed text-black max-w-2xl mx-auto" style={{fontFamily: 'Raleway, sans-serif'}}>
            Мы приглашаем Вас разделить с нами неповторимое торжество,
            посвящённое дню нашей свадьбы.
          </p>

          <div className="pt-12">
            <div className="text-6xl font-normal italic tracking-widest" style={{fontFamily: 'Cormorant Garamond, serif'}}>
              22.05.2026
            </div>
          </div>
        </div>
      </section>

      {/* Location Section */}
      <section className="relative py-24 px-6 min-h-screen flex items-center justify-center">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url("https://github.com/sergey-savelyev/weddingzal-cdn/blob/main/IMG_3203_BW.jpg?raw=true")',
          }}
        />

        <div className="relative z-10 max-w-4xl mx-auto w-full">
          <div className="bg-black/60 backdrop-blur-sm px-8 py-16 md:px-16 md:py-20">
          <h2 className="text-6xl font-normal italic text-center mb-10 tracking-wide" style={{fontFamily: 'Cormorant Garamond, serif'}}>
            Локация
          </h2>

          <div className="text-center space-y-6 mb-12">
            <h3 className="text-xl font-normal tracking-wide" style={{fontFamily: 'Raleway, sans-serif'}}>База отдыха Paradise</h3>
            <p className="text-xl italictext-white" style={{fontFamily: 'Raleway, sans-serif'}}>
              д. Забелинская, Архангельская область,<br />
              Котласский муниципальный округ
            </p>
          </div>

            <div className="text-center">
            <button
              onClick={openMap}
              className="inline-flex items-center gap-3 px-8 py-4 border border-white hover:bg-white hover:text-black transition-all duration-300 text-xl tracking-wide"
              style={{fontFamily: 'Raleway, sans-serif'}}
            >
              <MapPin size={20} />
              Посмотреть на карте
            </button>
          </div>
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-24 px-6 bg-white text-black">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-6xl font-normal italic text-center mb-20 tracking-wide" style={{fontFamily: 'Cormorant Garamond, serif'}}>
            Программа
          </h2>

          {/* Day 1 */}
          <div className="mb-24">
            <div className="text-center mb-16">
              <div className="text-5xl font-normal italic mb-2 text-black" style={{fontFamily: 'Cormorant Garamond, serif'}}>
                22.05.2026
              </div>
            </div>

            <div className="relative">
              <div className="absolute left-1/2 top-0 bottom-0 w-px bg-black -translate-x-1/2"></div>

              {[
                { time: '15:00', text: 'Фуршет', side: 'left' },
                { time: '15:30', text: 'Начало мероприятия', side: 'right' },
                { time: '16:00', text: 'Банкет', side: 'left' },
                { time: '00:00', text: 'Завершение вечера', side: 'right' }
              ].map((item, index) => (
                <div key={index} className="relative mb-12 last:mb-0">
                  <div className="flex items-center justify-center">
                    <div className={`w-5/12 ${item.side === 'left' ? 'text-right pr-12' : 'invisible'}`} style={{fontFamily: 'Raleway, sans-serif'}}>
                      <div className="text-xl font-bold mb-2 text-black">{item.side === 'left' ? item.time : ''}</div>
                      <div className="text-xl text-black">{item.side === 'left' ? item.text : ''}</div>
                    </div>

                    <div className="w-4 h-4 bg-black rounded-full border-4 border-white z-10"></div>

                    <div className={`w-5/12 ${item.side === 'right' ? 'text-left pl-12' : 'invisible'}`} style={{fontFamily: 'Raleway, sans-serif'}}>
                      <div className="text-xl font-bold mb-2 text-black">{item.side === 'right' ? item.time : ''}</div>
                      <div className="text-xl text-black">{item.side === 'right' ? item.text : ''}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Day 2 */}
          <div className="text-center pt-12 border-t border-black">
            <div className="text-5xl font-normal italic mb-6 text-black" style={{fontFamily: 'Cormorant Garamond, serif'}}>
              23.05.2026
            </div>
            <div className="text-xl font-bold mb-2 text-black" style={{fontFamily: 'Raleway, sans-serif'}}>15:00</div>
            <div className="text-xl text-black mb-4" style={{fontFamily: 'Raleway, sans-serif'}}>Праздник на природе</div>
            <p className="text-base text-black italic max-w-xl mx-auto" style={{fontFamily: 'Raleway, sans-serif'}}>
              * Второй день для отдыха в неформальной обстановке
            </p>
          </div>
        </div>
      </section>

      {/* Dress Code Section */}
      <section className="py-24 px-6 bg-white/5">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-6xl font-normal italic mb-12 tracking-wide" style={{fontFamily: 'Cormorant Garamond, serif'}}>
            Дресс-код
          </h2>
          
          <div className="text-2xl font-normal mb-8 tracking-widest" style={{fontFamily: 'Cormorant Garamond, serif'}}>TOTAL BLACK</div>

          <p className="text-xl font-normal leading-relaxed text-white max-w-2xl mx-auto mb-12" style={{fontFamily: 'Raleway, sans-serif'}}>
            Мы с большим трепетом готовимся к празднику и просим Вас учесть наши пожелания при выборе одежды и отдать предпочтение элегантным нарядам черного цвета.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
            <div
              className="aspect-square bg-cover bg-center rounded-sm"
              style={{
                backgroundImage: 'url("https://raw.githubusercontent.com/sergey-savelyev/weddingzal-cdn/refs/heads/main/IMG_4075.JPG")',
              }}
            />
            <div
              className="aspect-square bg-cover bg-center rounded-sm"
              style={{
                backgroundImage: 'url("https://raw.githubusercontent.com/sergey-savelyev/weddingzal-cdn/refs/heads/main/IMG_4082.JPG")',
              }}
            />
            <div
              className="aspect-square bg-cover bg-center rounded-sm"
              style={{
                backgroundImage: 'url("https://raw.githubusercontent.com/sergey-savelyev/weddingzal-cdn/refs/heads/main/IMG_4074.JPG")',
              }}
            />
            <div
              className="aspect-square bg-cover bg-center rounded-sm"
              style={{
                backgroundImage: 'url("https://raw.githubusercontent.com/sergey-savelyev/weddingzal-cdn/refs/heads/main/IMG_4079.JPG")',
              }}
            />
          </div>
        </div>
      </section>

      {/* Details Section */}
      <section className="py-24 px-6 bg-white text-black">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-6xl font-normal italic text-center mb-20 tracking-wide" style={{fontFamily: 'Cormorant Garamond, serif'}}>
            Детали
          </h2>

          <div className="space-y-16">
            {/* Detail 1 */}
            <div className="border-t border-black pt-8">
              <div className="flex justify-center mb-6">
                <img src="https://raw.githubusercontent.com/sergey-savelyev/weddingzal-cdn/refs/heads/main/envelope.png" alt="" className="w-16 h-16 opacity-70" />
              </div>
              <p className="text-xl text-black leading-relaxed text-center max-w-3xl mx-auto" style={{fontFamily: 'Raleway, sans-serif'}}>
                Пожалуйста, не обременяйте себя покупкой красивого букета или алкоголя, нам это ни к чему.
                Мы будем рады банальному, но практичному подарку в качестве конверта.
                Это поможет нам осуществить наши планы на ближайшее будущее.
              </p>
            </div>

            {/* Detail 2 */}
            <div className="border-t border-black pt-8">
              <div className="flex justify-center mb-6">
                <img src="https://raw.githubusercontent.com/sergey-savelyev/weddingzal-cdn/refs/heads/main/age.png" alt="" className="w-16 h-16 opacity-70" />
              </div>
              <p className="text-xl text-black leading-relaxed text-center max-w-3xl mx-auto" style={{fontFamily: 'Raleway, sans-serif'}}>
                Мы хотим, чтобы этот день стал для Вас особенным и незабываемым.
                Атмосфера вечера настроена на взрослую аудиторию и программа, к сожалению,
                не предусматривает присутствие детских аниматоров. Пожалуйста, оставьте своих
                деток с бабушками и дедушками или доверьте их своим друзьям. Мы искренне хотим,
                чтобы Вы насладились вечером в полной мере.
              </p>
            </div>

            {/* Detail 3 */}
            <div className="border-t border-black pt-8">
              <div className="flex justify-center mb-6">
                <img src="https://raw.githubusercontent.com/sergey-savelyev/weddingzal-cdn/refs/heads/main/speech.png" alt="" className="w-16 h-16 opacity-70" />
              </div>
              <p className="text-xl text-black leading-relaxed text-center max-w-3xl mx-auto" style={{fontFamily: 'Raleway, sans-serif'}}>
                Будем благодарны, если Вы воздержитесь от криков «Горько» на празднике,
                ведь поцелуй — это знак выражения чувств, он не может быть по заказу.
              </p>
            </div>

            {/* Detail 4 */}
            <div className="border-t border-black pt-8">
              <div className="flex justify-center mb-6">
                <img src="https://raw.githubusercontent.com/sergey-savelyev/weddingzal-cdn/refs/heads/main/house.png" alt="" className="w-16 h-16 opacity-70" />
              </div>
              <p className="text-xl text-black leading-relaxed text-center max-w-3xl mx-auto" style={{fontFamily: 'Raleway, sans-serif'}}>
                Второй день торжества также будет проходить на территории базы отдыха Paradise.
                Количество мест в гостевых домах ограничено, поэтому просим заполнить анкету ниже,
                если Вы хотите остаться с нами в этот прекрасный день
                (заезд 22.05 в 15:00, выезд 24.05 в 11:00).
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* RSVP Form Section */}
      <section className="py-24 px-6 bg-white/5">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-6xl font-normal italic text-center mb-16 tracking-wide" style={{fontFamily: 'Cormorant Garamond, serif'}}>
            Анкета
          </h2>

          

          {submitStatus === 'success' ? (
            <div className="text-center py-12">
              <div className="text-xl font-normal mb-4" style={{fontFamily: 'Raleway, sans-serif'}}>Спасибо!</div>
              <p className="text-xl text-white" style={{fontFamily: 'Raleway, sans-serif'}}>Ваш ответ получен. Ждём вас на празднике!</p>
            </div>
          ) : (
            <div>
              <p className="text-xl text-center font-normal text-white leading-relaxed max-w-2xl mx-auto mb-12" style={{fontFamily: 'Raleway, sans-serif'}}>
                Чтобы все прошло идеально, пожалуйста, ответьте на несколько вопросов и подтвердите свое присутствие до <b>01.04.2026</b>
              </p>
              <form onSubmit={handleSubmit} className="space-y-8" style={{fontFamily: 'Raleway, sans-serif'}}>
                {/* Attendance */}
                <div>
                  <label className="block text-xl mb-4 text-white">Планируете ли Вы присутствовать</label>
                  <div className="space-y-3">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.attendingDay1}
                        onChange={(e) => setFormData({...formData, attendingDay1: e.target.checked, notAttending: false})}
                        disabled={formData.notAttending}
                        className="w-5 h-5 disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                      <span className={formData.notAttending ? "text-white/40" : "text-white/80"}>На первом дне (22.05)</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.attendingDay2}
                        onChange={(e) => setFormData({...formData, attendingDay2: e.target.checked, notAttending: false})}
                        disabled={formData.notAttending}
                        className="w-5 h-5 disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                      <span className={formData.notAttending ? "text-white/40" : "text-white/80"}>На втором дне (23.05)</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.notAttending}
                        onChange={(e) => setFormData({...formData, notAttending: e.target.checked, attendingDay1: false, attendingDay2: false})}
                        className="w-5 h-5"
                      />
                      <span className="text-white/80">Не смогу присутствовать</span>
                    </label>
                  </div>
                </div>

                {/* Transfer */}
                <div>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.needsTransfer}
                      onChange={(e) => setFormData({...formData, needsTransfer: e.target.checked})}
                      className="w-5 h-5"
                    />
                    <span className="text-xl text-white">Понадобится ли Вам трансфер</span>
                  </label>
                </div>

                {/* Overnight */}
                <div>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.stayingOvernight}
                      onChange={(e) => setFormData({...formData, stayingOvernight: e.target.checked})}
                      className="w-5 h-5"
                    />
                    <span className="text-xl text-white">Планируете ли Вы оставаться на ночь</span>
                  </label>
                </div>

                {/* Allergies */}
                <div>
                  <label className="block text-xl mb-3 text-white">Есть ли у Вас пищевые аллергии</label>
                  <textarea
                    value={formData.allergies}
                    onChange={(e) => setFormData({...formData, allergies: e.target.value})}
                    rows={3}
                    className="w-full bg-transparent border border-white/20 px-6 py-4 focus:border-white/60 focus:outline-none transition-colors resize-none"
                    placeholder="Укажите, если есть"
                  />
                </div>

                {/* Alcohol Preference */}
                <div>
                  <label className="block text-xl mb-3 text-white">Какой алкоголь предпочитаете</label>
                  <input
                    type="text"
                    value={formData.alcoholPreference}
                    onChange={(e) => setFormData({...formData, alcoholPreference: e.target.value})}
                    className="w-full bg-transparent border border-white/20 px-6 py-4 focus:border-white/60 focus:outline-none transition-colors"
                    placeholder="Например: вино, виски, не пью"
                  />
                </div>

                {/* Submit Button */}
                <div className="pt-8">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-5 border border-white hover:bg-white hover:text-black transition-all duration-300 text-xl tracking-wide disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Отправка...' : (isFormSubmitted ? 'Обновить данные' : 'Отправить')}
                  </button>
                </div>

                {submitStatus === 'error' && (
                  <p className="text-red-400 text-center">Произошла ошибка. Пожалуйста, попробуйте снова.</p>
                )}
              </form>
            </div>
          )}
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 px-6 bg-white text-black">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-6xl font-normal italic mb-12 tracking-wide" style={{fontFamily: 'Cormorant Garamond, serif'}}>
            Контакты
          </h2>

          <p className="text-xl text-black leading-relaxed max-w-2xl mx-auto mb-8" style={{fontFamily: 'Raleway, sans-serif'}}>
            В день торжества мы будем немного заняты, поэтому в случае возникновения вопросов
            Вы всегда можете обратиться за помощью к нашему координатору:
          </p>

          <div className="flex items-center justify-center gap-3 text-xl" style={{fontFamily: 'Raleway, sans-serif'}}>
            <Phone size={20} className="text-black/60" />
            <span className="font-bold">Анастасия</span>
            <a href="tel:+79116802837" className="hover:text-black/60 transition-colors font-bold">
              +7 911 680 28 37
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 text-center text-white/40 text-base border-t border-white/10">
        <p>Тимофей & Дарья • 22.05.2026</p>
      </footer>

      <style jsx>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@300;400;500;600;700&family=Crimson+Text:wght@400;600;700&family=Inter:wght@300;400;500;600&display=swap');

        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translateY(50px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in {
          animation: fade-in 1.5s ease-out;
        }

        .animate-slide-up {
          animation: slide-up 1s ease-out;
        }

        input[type="checkbox"] {
          appearance: none;
          border: 1px solid rgba(255, 255, 255, 0.3);
          background: transparent;
          cursor: pointer;
          position: relative;
        }

        input[type="checkbox"]:checked {
          background: white;
        }

        input[type="checkbox"]:checked::after {
          content: '✓';
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          color: black;
          font-size: 14px;
        }
      `}</style>
    </div>
  );
};

export default WeddingInvitation;
